// ==========================================================
// Web Audio API Synthesized ASMR Sound Effects for Paint Meister
// ==========================================================

export class SoundManager {
  private ctx: AudioContext | null = null;
  private enabled: boolean = true;
  private brushGain: GainNode | null = null;
  private sprayNoiseNode: AudioNode | null = null;
  private sprayGain: GainNode | null = null;
  private isBrushPlaying: boolean = false;
  private isSprayPlaying: boolean = false;

  constructor() {
    // Lazy initialize on first user gesture
  }

  private initCtx() {
    if (!this.ctx) {
      const AudioCtxClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (AudioCtxClass) {
        this.ctx = new AudioCtxClass();
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  }

  public toggleSound(): boolean {
    this.enabled = !this.enabled;
    if (!this.enabled) {
      this.stopContinuousSounds();
    }
    return this.enabled;
  }

  public isSoundEnabled(): boolean {
    return this.enabled;
  }

  // --- Procedural Sound Generators ---

  // 1. UI Click / Tap
  public playClick() {
    if (!this.enabled) return;
    this.initCtx();
    if (!this.ctx) return;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(800, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(400, this.ctx.currentTime + 0.04);

    gain.gain.setValueAtTime(0.12, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.04);

    osc.connect(gain);
    gain.connect(this.ctx.destination);

    osc.start();
    osc.stop(this.ctx.currentTime + 0.04);
  }

  // 2. Continuous Brush Stroke (Soft friction / paint squish)
  public startBrushStroke() {
    if (!this.enabled || this.isBrushPlaying) return;
    this.initCtx();
    if (!this.ctx) return;

    try {
      const bufferSize = this.ctx.sampleRate * 1;
      const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
      const output = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        output[i] = Math.random() * 2 - 1; // Pink/white noise
      }

      const whiteNoise = this.ctx.createBufferSource();
      whiteNoise.buffer = buffer;
      whiteNoise.loop = true;

      // Lowpass filter for smooth squishy texture
      const filter = this.ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.setValueAtTime(450, this.ctx.currentTime);
      filter.Q.setValueAtTime(1.5, this.ctx.currentTime);

      this.brushGain = this.ctx.createGain();
      this.brushGain.gain.setValueAtTime(0.01, this.ctx.currentTime);
      this.brushGain.gain.linearRampToValueAtTime(0.08, this.ctx.currentTime + 0.05);

      whiteNoise.connect(filter);
      filter.connect(this.brushGain);
      this.brushGain.connect(this.ctx.destination);

      whiteNoise.start();
      this.sprayNoiseNode = whiteNoise;
      this.isBrushPlaying = true;
    } catch {
      // Audio context might be restricted before gesture
    }
  }

  public updateBrushVelocity(speed: number) {
    if (!this.isBrushPlaying || !this.brushGain || !this.ctx) return;
    const clamped = Math.min(Math.max(speed / 20, 0.03), 0.12);
    this.brushGain.gain.setTargetAtTime(clamped, this.ctx.currentTime, 0.05);
  }

  public stopBrushStroke() {
    if (!this.isBrushPlaying) return;
    if (this.brushGain && this.ctx) {
      this.brushGain.gain.linearRampToValueAtTime(0.001, this.ctx.currentTime + 0.05);
    }
    setTimeout(() => {
      if (this.sprayNoiseNode) {
        try {
          (this.sprayNoiseNode as AudioBufferSourceNode).stop();
        } catch {
          // Ignored
        }
      }
      this.isBrushPlaying = false;
    }, 60);
  }

  // 3. Spray Gun Sound (Airbrush Hiss)
  public startSpray() {
    if (!this.enabled || this.isSprayPlaying) return;
    this.initCtx();
    if (!this.ctx) return;

    try {
      const bufferSize = this.ctx.sampleRate * 1;
      const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
      const output = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        output[i] = (Math.random() * 2 - 1) * 0.5;
      }

      const noise = this.ctx.createBufferSource();
      noise.buffer = buffer;
      noise.loop = true;

      const highpass = this.ctx.createBiquadFilter();
      highpass.type = 'highpass';
      highpass.frequency.setValueAtTime(1200, this.ctx.currentTime);

      this.sprayGain = this.ctx.createGain();
      this.sprayGain.gain.setValueAtTime(0.01, this.ctx.currentTime);
      this.sprayGain.gain.linearRampToValueAtTime(0.1, this.ctx.currentTime + 0.05);

      noise.connect(highpass);
      highpass.connect(this.sprayGain);
      this.sprayGain.connect(this.ctx.destination);

      noise.start();
      this.isSprayPlaying = true;
    } catch {
      // Ignored
    }
  }

  public stopSpray() {
    if (!this.isSprayPlaying) return;
    if (this.sprayGain && this.ctx) {
      this.sprayGain.gain.linearRampToValueAtTime(0.001, this.ctx.currentTime + 0.05);
    }
    setTimeout(() => {
      this.isSprayPlaying = false;
    }, 60);
  }

  // 4. Masking Tape Peel (Crisp adhesive rip sound - ASMR)
  public playTapePeel() {
    if (!this.enabled) return;
    this.initCtx();
    if (!this.ctx) return;

    const bufferSize = Math.floor(this.ctx.sampleRate * 0.25);
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const output = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      // Crackle bursts
      output[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.4)) * (Math.random() > 0.3 ? 1 : 0);
    }

    const noise = this.ctx.createBufferSource();
    noise.buffer = buffer;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.setValueAtTime(2200, this.ctx.currentTime);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.18, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.24);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.ctx.destination);

    noise.start();
  }

  // 5. Masking Tape Place (Snappy tape stretch & stick)
  public playTapePlace() {
    if (!this.enabled) return;
    this.initCtx();
    if (!this.ctx) return;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'triangle';
    osc.frequency.setValueAtTime(220, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(650, this.ctx.currentTime + 0.08);

    gain.gain.setValueAtTime(0.12, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.08);

    osc.connect(gain);
    gain.connect(this.ctx.destination);

    osc.start();
    osc.stop(this.ctx.currentTime + 0.08);
  }

  // 6. Pipette / Droplet Drop Sound
  public playDrop() {
    if (!this.enabled) return;
    this.initCtx();
    if (!this.ctx) return;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(1400, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(450, this.ctx.currentTime + 0.12);

    gain.gain.setValueAtTime(0.2, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.12);

    osc.connect(gain);
    gain.connect(this.ctx.destination);

    osc.start();
    osc.stop(this.ctx.currentTime + 0.12);
  }

  // 7. Stir / Mix Swirl Sound
  public playStir() {
    if (!this.enabled) return;
    this.initCtx();
    if (!this.ctx) return;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(320, this.ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(580, this.ctx.currentTime + 0.2);
    osc.frequency.linearRampToValueAtTime(300, this.ctx.currentTime + 0.4);

    gain.gain.setValueAtTime(0.08, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.4);

    osc.connect(gain);
    gain.connect(this.ctx.destination);

    osc.start();
    osc.stop(this.ctx.currentTime + 0.4);
  }

  // 8. Level Clear / Meister Fanfare (Chord)
  public playFanfare(stars: number) {
    if (!this.enabled) return;
    this.initCtx();
    if (!this.ctx) return;

    const chords = [
      [523.25, 659.25, 783.99, 1046.50], // C Major
      [587.33, 739.99, 880.00, 1174.66], // D Major
      [659.25, 830.61, 987.77, 1318.51]  // E Major (Grand)
    ];

    const targetChord = chords[Math.min(stars - 1, 2)] || chords[0];

    targetChord.forEach((freq, idx) => {
      const osc = this.ctx!.createOscillator();
      const gain = this.ctx!.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, this.ctx!.currentTime + idx * 0.08);

      gain.gain.setValueAtTime(0.001, this.ctx!.currentTime);
      gain.gain.linearRampToValueAtTime(0.12, this.ctx!.currentTime + idx * 0.08 + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, this.ctx!.currentTime + 1.2);

      osc.connect(gain);
      gain.connect(this.ctx!.destination);

      osc.start(this.ctx!.currentTime + idx * 0.08);
      osc.stop(this.ctx!.currentTime + 1.2);
    });
  }

  public stopContinuousSounds() {
    this.stopBrushStroke();
    this.stopSpray();
  }
}

export const sounds = new SoundManager();
